import React, { createContext, useCallback, useState, useContext } from "react";

export type SubmenuKeys =
  | "home"
  | "orders"
  | "dealers"
  | "reports"
  | "products"
  | "integrations";

type IsSubmenuOpenProps = {
  [key in SubmenuKeys]: boolean;
};

interface SidebarProviderProps {
  children: React.ReactNode;
}

interface SidebarContextProps {
  isSidebarMaximized: boolean;
  handleToggleSidebar(): void;
  setIsSubmenuOpen: React.Dispatch<React.SetStateAction<IsSubmenuOpenProps>>;
  isSubmenuOpen: IsSubmenuOpenProps;
}

const SidebarContext = createContext<SidebarContextProps>(
  {} as SidebarContextProps,
);

export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const [isSidebarMaximized, setIsSidebarMaximized] = useState<boolean>(() => {
    let isSidebarMax: string | boolean | null =
      localStorage.getItem("@ctech:sidebar");

    if (isSidebarMax !== null) {
      isSidebarMax = Boolean(JSON.parse(isSidebarMax));
      return isSidebarMax;
    }
    return true;
  });

  const [isSubmenuOpen, setIsSubmenuOpen] = React.useState({
    orders: false,
    dealers: false,
    reports: false,
    home: false,
    products: false,
    integrations: false,
  });

  const handleToggleSidebar = useCallback(() => {
    localStorage.setItem("@ctech:sidebar", (!isSidebarMaximized).toString());

    setIsSidebarMaximized((sidebarState) => {
      if (sidebarState) {
        setIsSubmenuOpen({
          orders: false,
          dealers: false,
          reports: false,
          home: false,
          products: false,
          integrations: false,
        });
      }

      return !sidebarState;
    });
  }, [isSidebarMaximized]);

  return (
    <SidebarContext.Provider
      value={{
        isSidebarMaximized,
        handleToggleSidebar,
        isSubmenuOpen,
        setIsSubmenuOpen,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export function useSidebar(): SidebarContextProps {
  const context = useContext(SidebarContext);

  if (!context) {
    throw new Error("useSidebar must be used within a SidebarContext");
  }
  return context;
}
